import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CloseOutlined } from '@ant-design/icons';
import { Button, Progress, Result } from 'antd';

import { i18, numberFormat, SIGN_CANCEL_TYPES, translationGroups, trb } from '../../../Utilities';
import { Number } from '../../../Components';
import { textColors } from '../../../Config';

export const OperationApprovalMobile = ({ code, cancel, errorCode, approveSum, horizontal, approveCount, submit, dontScrollToTop, RQ_ID }) => {
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  const [secondsEnd, setSecondsEnd] = useState(false);
  const ref = useRef(null);
  const submissionInProgress = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (errorCode) {
      return;
    }

    const scrollToRef = () => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    scrollToRef();

    return () => {
      if (!dontScrollToTop) {
        window.scrollTo(0, 0);
      }
    };
  }, [errorCode, dontScrollToTop]);

  useEffect(() => {
    if (errorCode) {
      return;
    }

    const tick = () => {
      setSecondsRemaining((prev) => {
        const newValue = Math.max(prev - 1, 0);

        if (newValue === 0) {
          setSecondsEnd(true);
          cancel(SIGN_CANCEL_TYPES.TIMEOUT);
        }

        return newValue;
      });
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [errorCode, cancel]);

  const handleSubmit = useCallback(async () => {
    if (submissionInProgress.current || !mountedRef.current) {
      return;
    }

    submissionInProgress.current = true;

    try {
      return await submit();
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    } finally {
      if (mountedRef.current) {
        submissionInProgress.current = false;
      }
    }
  }, [submit]);

  useEffect(() => {
    if (errorCode || !code || !mountedRef.current) {
      return;
    }

    let timeoutId;
    let intervalId;

    const startPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
      }

      const executePolling = async () => {
        if (mountedRef.current && !submissionInProgress.current) {
          await handleSubmit();
        }
      };

      intervalId = setInterval(executePolling, 1000);
    };

    const delay = code === '0000' ? 0 : 1000;
    timeoutId = setTimeout(startPolling, delay);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [code, errorCode, handleSubmit]);

  const formatProgress = () => {
    if (secondsEnd) {
      return <CloseOutlined aria-hidden="true" />;
    }

    return `${Math.round(secondsRemaining)}${trb(i18.Labels.SecondsShort)}`;
  };

  const handleCancel = () => {
    cancel(SIGN_CANCEL_TYPES.MANUAL);
  };

  return (
    <div
      ref={ref}
      className={horizontal ? 'd-flex flex-row justify-content-around align-items-center' : null}
      style={
        horizontal
          ? { maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }
          : {
              textAlign: 'center',
              marginBottom: '24px',
            }
      }
    >
      {code ? (
        <>
          <div
            style={{ marginBottom: '24px' }}
            className={horizontal ? 'd-flex flex-row align-items-center' : null}
          >
            <Progress
              type="circle"
              percent={(100 / 60) * secondsRemaining}
              status={secondsEnd ? 'exception' : 'active'}
              format={formatProgress}
              size={100}
              style={horizontal ? null : { marginBottom: '24px' }}
              strokeColor={textColors.primary}
            />
            <div className={horizontal ? 'd-flex flex-column text-left p-3' : null}>
              {approveCount && (
                <div>
                  {trb(i18.Labels.ApprovalPaymentsCount)}:{' '}
                  <Number
                    value={approveCount}
                    format={numberFormat().number}
                    text={1}
                  />
                </div>
              )}
              {approveSum?.sum && (
                <div>
                  {trb(i18.Labels.ApprovalSum)}: {approveSum.sum} {approveSum.currency}
                </div>
              )}
              <div>{trb(i18.Messages.ApproveOperationOnYourDevice)}</div>
              <div>
                {trb(i18.Messages.ControlCode)} <strong>{code}</strong>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <Button
              id="OperationApprovalMobileCancelButton"
              onClick={handleCancel}
            >
              <CloseOutlined aria-hidden="true" />
              {trb(i18.Buttons.Cancel)}
            </Button>
          </div>
        </>
      ) : (
        <>
          {errorCode && (
            <Result
              status="error"
              title={trb(translationGroups.ErrorTitles + errorCode)}
              subTitle={trb(translationGroups.ErrorDescriptions + errorCode)}
            />
          )}
          {!errorCode && RQ_ID && (
            <Result
              status="error"
              title={trb(i18.ErrorTitles.OperationApprovalMobileNoCode)}
              subTitle={trb(i18.ErrorDescriptions.OperationApprovalMobileNoCode)}
            />
          )}
        </>
      )}
    </div>
  );
};

OperationApprovalMobile.propTypes = {
  code: PropTypes.string.isRequired,
  cancel: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
  approveSum: PropTypes.shape({
    sum: PropTypes.string,
    currency: PropTypes.string,
  }),
  horizontal: PropTypes.bool,
  approveCount: PropTypes.number,
  dontScrollToTop: PropTypes.bool,
  RQ_ID: PropTypes.string,
};
